import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Calendar, Package, MapPin, FileText, Users, Trash2, X, Activity, CheckCircle, Menu, Truck, BarChart3 } from 'lucide-react';
import { callWFAPI } from '../utils/helper';
import { InvoiceManager } from './InvoiceManager';
import { USMap } from './USMap';
import { NotificationTemplates } from './NotificationTemplates';
import { OnsitePrep } from './OnsitePrep';
import { RentalsManager } from './RentalsManager';
import { CalendarView } from './CalendarView';
import { DistributorsManager } from './DistributorsManager';
import { TechniciansManager } from './TechniciansManager';
import { ActivityLog } from './ActivityLog';
import { DispatchManager } from './DispatchManager';
import { InternalNotesButton } from './InternalNotesButton';
import { SalesReport } from './SalesReport';
interface Rental {
  id: string;
  name: string;
  email: string;
  phone: string;
  eventName: string;
  customerInfo?: {
    address?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    eventVenueCityState?: string;
    preferredTechnician?: string;
  };
  proposedEventDate: string;
  alternateDate: string;
  status: 'Pending' | 'Confirmed' | 'Deposit' | 'Paid in Full';
  total: number;
  subtotal: number;
  discount: number;
  tax: number;
  added: string;
  assignedTechnician?: string;
  assignedDistributor?: string;
  invoiceId?: string;
  internalNotes?: string;
  selections: {
    gen3Body: number;
    gen3Head: number;
    gen3JudgesOnly: number;
    fullGen3Ring: number;
    requireTech: boolean;
    numberOfDays: number;
  };
}
interface ActivityLogEntry {
  id: string;
  timestamp: string;
  action: string;
  category: 'technician' | 'distributor' | 'invoice' | 'rental' | 'system' | 'dispatch' | 'onsiteprep';
  details: string;
  user: string;
}
export function AdminPanel() {
  const navigate = useNavigate();
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRental, setSelectedRental] = useState<Rental | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'rentals' | 'calendar' | 'invoices' | 'distributors' | 'technicians' | 'log' | 'map' | 'dispatch' | 'onsiteprep' | 'notifications' | 'sales'>('rentals');
  const [invoices, setInvoices] = useState<any[]>([]);
  const [technicians, setTechnicians] = useState<Array<{
    id: string;
    name: string;
    distributorId: string;
    distributorName: string;
    email: string;
    phone: string;
  }>>([]);
  const [distributors, setDistributors] = useState<Array<{
    id: string;
    name: string;
    rings: number;
    stripePublishableKey: string;
    stripeSecretKey: string;
  }>>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLogEntry[]>([]);
  const [logFilter, setLogFilter] = useState<string>('all');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningTechnician, setAssigningTechnician] = useState('');
  const [assigningDistributor, setAssigningDistributor] = useState('');
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [editingDates, setEditingDates] = useState(false);
  const [editedProposedDate, setEditedProposedDate] = useState('');
  const [editedAlternateDate, setEditedAlternateDate] = useState('');
  const [editedNumberOfDays, setEditedNumberOfDays] = useState(1);
  const formatPhoneNumber = (phone: string): string => {
    if (!phone) return '';
    let cleaned = phone.replace(/[^\d+]/g, '');
    if (!cleaned.startsWith('+')) {
      cleaned = '+1' + cleaned;
    }
    return cleaned;
  };
  useEffect(() => {
    loadRentals();
    loadInvoices();
    loadDistributors();
    loadTechnicians();
    loadActivityLogs();
    logActivity('system', 'Admin panel accessed', 'User opened admin panel');
  }, []);
  // Cross-reference rentals with technicians to fill in missing distributor info
  // This handles the case where a customer selected a preferred technician
  // but Zoho doesn't resolve the nested Technician→Distributor lookup
  useEffect(() => {
    if (technicians.length === 0 || rentals.length === 0) return;
    let needsUpdate = false;
    const updatedRentals = rentals.map(rental => {
      // If technician is assigned but distributor is missing, resolve it
      if (rental.assignedTechnician && !rental.assignedDistributor) {
        const tech = technicians.find(t => t.name === rental.assignedTechnician);
        if (tech && tech.distributorName) {
          needsUpdate = true;
          return {
            ...rental,
            assignedDistributor: tech.distributorName
          };
        }
      }
      return rental;
    });
    if (needsUpdate) {
      setRentals(updatedRentals);
    }
  }, [technicians, rentals.length]);
  const loadDistributors = async () => {
    try {
      const data = {
        appname: 'tkd-score',
        reportname: 'All_Distributors'
      };
      const resp = await callWFAPI(0, 'zohoCreator', data);
      const loadedDistributors = resp.map((item: any) => ({
        id: item.ID,
        name: item.Name,
        rings: parseInt(item.Rings) || 0,
        stripePublishableKey: item.Stripe_Public_Key || '',
        stripeSecretKey: item.Stripe_Private_Key || ''
      }));
      setDistributors(loadedDistributors);
    } catch (error) {
      console.error('Error loading distributors:', error);
      setDistributors([]);
    }
  };
  const loadTechnicians = async () => {
    try {
      const data = {
        appname: 'tkd-score',
        reportname: 'All_Technicians'
      };
      const resp = await callWFAPI(0, 'zohoCreator', data);
      const loadedTechnicians = resp.map((item: any) => ({
        id: item.ID,
        name: item.Name,
        distributorId: item.Distributor?.ID || '',
        distributorName: item.Distributor?.display_value || '',
        email: item.Email || '',
        phone: item.Phone_Number || ''
      }));
      setTechnicians(loadedTechnicians);
    } catch (error) {
      console.error('Error loading technicians:', error);
      setTechnicians([]);
    }
  };
  const loadInvoices = async () => {
    try {
      const data = {
        appname: 'tkd-score',
        reportname: 'All_Rental_Invoices'
      };
      const resp = await callWFAPI(0, 'zohoCreator', data);
      if (!resp || !Array.isArray(resp)) {
        setInvoices([]);
        return;
      }
      const loadedInvoices = resp.map((item: any) => {
        const lineItems = (item.Items || []).map((lineItem: any) => {
          const parts = lineItem.display_value.split('||');
          const title = parts[0] || '';
          const quantity = parseInt(parts[1]) || 0;
          const price = parseFloat(parts[2]) || 0;
          return {
            description: title,
            quantity: quantity,
            price: price,
            total: quantity * price
          };
        });
        const totalAmount = parseFloat(item.Total_Amount || 0);
        const paidAmount = parseFloat(item.Paid_Amount || 0);
        const balance = totalAmount - paidAmount;
        let status = 'Not Paid';
        if (paidAmount > 0 && balance > 0.01) {
          status = 'Partially Paid';
        } else if (balance <= 0.01) {
          status = 'Paid';
        }
        return {
          id: item.ID,
          invoiceNumber: item.Number || `INV-${item.ID}`,
          customerName: item.Customer_Name,
          customerEmail: item.Customer_Email,
          status: status,
          lineItems: lineItems,
          subtotal: parseFloat(item.Subtotal || 0),
          discount: parseFloat(item.Discount_Amount || 0),
          tax: parseFloat(item.California_Tax || 0),
          total: totalAmount,
          amountPaid: paidAmount,
          balance: balance,
          created: item.Added_Time
        };
      });
      setInvoices(loadedInvoices);
    } catch (error) {
      console.error('Error loading invoices:', error);
      setInvoices([]);
    }
  };
  const loadRentals = async () => {
    setLoading(true);
    try {
      const data = {
        appname: 'tkd-score',
        reportname: 'Gen_3_Scoring_Rental_Report',
        criteria: ''
      };
      const resp = await callWFAPI(0, 'zohoCreator', data);
      const loadedRentals: Rental[] = resp.map((item: any) => {
        const shippingAddress = item.Shipping_Address || {};
        // Handle Technician field - could be an object with display_value or a raw string/ID
        let techName: string | undefined;
        let techDistributor: string | undefined;
        if (item.Technician) {
          if (typeof item.Technician === 'object') {
            techName = item.Technician.display_value || undefined;
            techDistributor = item.Technician.Distributor?.display_value || undefined;
          } else if (typeof item.Technician === 'string' && item.Technician.trim()) {
            // Raw string - could be a name or ID, store it for now
            techName = item.Technician;
          }
        }
        return {
          id: item.ID,
          name: item.Name,
          email: item.Email,
          phone: item.Phone_Number,
          eventName: item.Event_Name || 'N/A',
          customerInfo: {
            address: shippingAddress.address_line_1 || undefined,
            address2: shippingAddress.address_line_2 || undefined,
            city: shippingAddress.district_city || undefined,
            state: shippingAddress.state_province || undefined,
            zip: shippingAddress.postal_code || undefined,
            eventVenueCityState: item.Event_Venue_City_State || undefined,
            preferredTechnician: techName
          },
          proposedEventDate: item.Proposed_Event_Date || '',
          alternateDate: item.Alternate_Date || '',
          status: item.Status || 'Pending',
          total: parseFloat(item.Total_Amount || 0),
          subtotal: parseFloat(item.Subtotal || 0),
          discount: parseFloat(item.Discount_Amount || 0),
          tax: parseFloat(item.California_Tax || 0),
          added: item.added,
          assignedTechnician: techName,
          assignedDistributor: techDistributor,
          invoiceId: item.Rental_Invoice?.ID || undefined,
          internalNotes: item.Internal_Notes || '',
          selections: {
            gen3Body: parseInt(item.Gen3_Body_Quantity) || 0,
            gen3Head: parseInt(item.Gen3_Head_Quantity) || 0,
            gen3JudgesOnly: parseInt(item.Gen3_Judges_Only_Quantity) || 0,
            fullGen3Ring: parseInt(item.Full_Gen3_Ring_Quantity) || 0,
            requireTech: item.Requires_Technician === 'true',
            numberOfDays: parseInt(item.Number_Of_Days) || 1
          }
        };
      });
      setRentals(loadedRentals);
    } catch (error) {
      console.error('Error loading rentals:', error);
    } finally {
      setLoading(false);
    }
  };
  const loadActivityLogs = async () => {
    try {
      const data = {
        appname: 'tkd-score',
        reportname: 'All_Rental_Activity_Logs'
      };
      const resp = await callWFAPI(0, 'zohoCreator', data);
      const loadedLogs: ActivityLogEntry[] = resp.map((item: any) => ({
        id: item.ID,
        timestamp: item.Added_Time,
        action: item.action_field,
        category: item.Category as ActivityLogEntry['category'],
        details: item.Details,
        user: item.User || 'Admin'
      }));
      setActivityLogs(loadedLogs);
    } catch (error) {
      console.error('Error loading activity logs:', error);
      setActivityLogs([]);
    }
  };
  const logActivity = async (category: ActivityLogEntry['category'], action: string, details: string) => {
    try {
      const data = {
        appname: 'tkd-score',
        formname: 'Rental_Activity_Log',
        params: JSON.stringify({
          User: 'Admin',
          action_field: action,
          Category: category,
          Details: details
        })
      };
      await callWFAPI(0, 'zohoCreator', data);
      await loadActivityLogs();
    } catch (error) {
      console.error('Error logging activity:', error);
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Confirmed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Deposit':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Paid in Full':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Pending':
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'Confirmed':
        return 'bg-blue-500';
      case 'Deposit':
        return 'bg-yellow-500';
      case 'Paid in Full':
        return 'bg-green-500';
      case 'Pending':
      default:
        return 'bg-slate-400';
    }
  };
  const getDistributorColor = (distributor: string) => {
    switch (distributor) {
      case 'Daedo Florida':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'TKDScore':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Truescore':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'None':
        return 'bg-slate-100 text-slate-600 border-slate-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };
  const getCoastFromLocation = (location: string): 'East Coast' | 'West Coast' | 'Central' | 'Unknown' => {
    const loc = location.toLowerCase();
    const westCoastStates = ['california', 'ca', 'oregon', 'or', 'washington', 'wa', 'nevada', 'nv', 'alaska', 'ak', 'hawaii', 'hi'];
    const eastCoastStates = ['maine', 'me', 'new hampshire', 'nh', 'vermont', 'vt', 'massachusetts', 'ma', 'rhode island', 'ri', 'connecticut', 'ct', 'new york', 'ny', 'new jersey', 'nj', 'pennsylvania', 'pa', 'delaware', 'de', 'maryland', 'md', 'virginia', 'va', 'north carolina', 'nc', 'south carolina', 'sc', 'georgia', 'ga', 'florida', 'fl'];
    const centralStates = ['montana', 'mt', 'idaho', 'id', 'wyoming', 'wy', 'utah', 'ut', 'colorado', 'co', 'arizona', 'az', 'new mexico', 'nm', 'north dakota', 'nd', 'south dakota', 'sd', 'nebraska', 'ne', 'kansas', 'ks', 'oklahoma', 'ok', 'texas', 'tx', 'minnesota', 'mn', 'iowa', 'ia', 'missouri', 'mo', 'arkansas', 'ar', 'louisiana', 'la', 'wisconsin', 'wi', 'illinois', 'il', 'michigan', 'mi', 'indiana', 'in', 'ohio', 'oh', 'kentucky', 'ky', 'tennessee', 'tn', 'mississippi', 'ms', 'alabama', 'al', 'west virginia', 'wv'];
    for (const state of westCoastStates) {
      if (loc.includes(state)) return 'West Coast';
    }
    for (const state of eastCoastStates) {
      if (loc.includes(state)) return 'East Coast';
    }
    for (const state of centralStates) {
      if (loc.includes(state)) return 'Central';
    }
    return 'Unknown';
  };
  const createInvoiceFromRental = async (rental: Rental) => {
    setCreatingInvoice(true);
    try {
      const tech = technicians.find(t => t.name === rental.assignedTechnician);
      if (!tech) {
        alert('Assigned technician not found. Please assign a technician first.');
        setCreatingInvoice(false);
        return;
      }
      const lineItems: any[] = [];
      // Day 1 Equipment (full price)
      if (rental.selections.fullGen3Ring > 0) {
        lineItems.push({
          Title: rental.selections.numberOfDays > 1 ? 'Full Gen 3 Ring (Day 1)' : 'Full Gen 3 Ring',
          Quantity: rental.selections.fullGen3Ring,
          Price: 900
        });
      }
      if (rental.selections.gen3Body > 0) {
        lineItems.push({
          Title: rental.selections.numberOfDays > 1 ? 'Gen 3 Body (Day 1)' : 'Gen 3 Body',
          Quantity: rental.selections.gen3Body,
          Price: 700
        });
      }
      if (rental.selections.gen3Head > 0) {
        lineItems.push({
          Title: rental.selections.numberOfDays > 1 ? 'Gen 3 Head (Day 1)' : 'Gen 3 Head',
          Quantity: rental.selections.gen3Head,
          Price: 425
        });
      }
      if (rental.selections.gen3JudgesOnly > 0) {
        lineItems.push({
          Title: rental.selections.numberOfDays > 1 ? 'Gen 3 Judges Only (Day 1)' : 'Gen 3 Judges Only',
          Quantity: rental.selections.gen3JudgesOnly,
          Price: 350
        });
      }
      // Additional Days Equipment (50% price) - only if multi-day event
      if (rental.selections.numberOfDays > 1) {
        if (rental.selections.fullGen3Ring > 0) {
          lineItems.push({
            Title: `Full Gen 3 Ring (Days 2-${rental.selections.numberOfDays})`,
            Quantity: rental.selections.fullGen3Ring * (rental.selections.numberOfDays - 1),
            Price: 450 // 50% of 900
          });
        }
        if (rental.selections.gen3Body > 0) {
          lineItems.push({
            Title: `Gen 3 Body (Days 2-${rental.selections.numberOfDays})`,
            Quantity: rental.selections.gen3Body * (rental.selections.numberOfDays - 1),
            Price: 350 // 50% of 700
          });
        }
        if (rental.selections.gen3Head > 0) {
          lineItems.push({
            Title: `Gen 3 Head (Days 2-${rental.selections.numberOfDays})`,
            Quantity: rental.selections.gen3Head * (rental.selections.numberOfDays - 1),
            Price: 212.5 // 50% of 425
          });
        }
        if (rental.selections.gen3JudgesOnly > 0) {
          lineItems.push({
            Title: `Gen 3 Judges Only (Days 2-${rental.selections.numberOfDays})`,
            Quantity: rental.selections.gen3JudgesOnly * (rental.selections.numberOfDays - 1),
            Price: 175 // 50% of 350
          });
        }
      }
      // Technician (multiplied by number of days)
      if (rental.selections.requireTech) {
        lineItems.push({
          Title: rental.selections.numberOfDays > 1 ? `On-site Technician (${rental.selections.numberOfDays} Days)` : 'On-site Technician',
          Quantity: 1,
          Price: 500 * rental.selections.numberOfDays
        });
      }
      const invoiceData = {
        appname: 'tkd-score',
        formname: 'Rental_Invoice',
        params: JSON.stringify({
          Customer_Name: rental.name,
          Customer_Email: rental.email,
          Gen_3_Scoring_Rental: rental.id,
          Technician: tech.id,
          Subtotal: rental.subtotal,
          California_Tax: rental.tax,
          Discount_Amount: rental.discount,
          Total_Amount: rental.total,
          Distributor: tech.distributorId,
          Items: lineItems
        })
      };
      const newInvoice = await callWFAPI(0, 'zohoCreator', invoiceData);
      // Link the rental to the invoice
      const rentalUpdateData = {
        appname: 'tkd-score',
        reportname: 'Gen_3_Scoring_Rental_Report',
        id: rental.id,
        params: JSON.stringify({
          Rental_Invoice: newInvoice.ID
        })
      };
      await callWFAPI(0, 'zohoCreator', rentalUpdateData);
      await loadInvoices();
      await loadRentals();
      logActivity('invoice', 'Invoice created', `Created invoice for ${rental.name} - $${rental.total.toFixed(2)}${rental.selections.numberOfDays > 1 ? ` (${rental.selections.numberOfDays}-Day Event)` : ''}`);
      setActiveTab('invoices');
    } catch (error) {
      console.error('Error creating invoice:', error);
      alert('Failed to create invoice. Please try again.');
    } finally {
      setCreatingInvoice(false);
    }
  };
  const hasInvoice = (rentalId: string) => {
    const rental = rentals.find(r => r.id === rentalId);
    return rental?.invoiceId ? true : false;
  };
  const deleteRental = async (rental: Rental) => {
    if (rental.invoiceId) {
      alert('Cannot delete a rental that has an invoice. Please delete the invoice first.');
      return;
    }
    if (window.confirm(`Are you sure you want to delete the rental for ${rental.name} - ${rental.eventName}? This action cannot be undone.`)) {
      try {
        const data = {
          appname: 'tkd-score',
          reportname: 'Gen_3_Scoring_Rental_Report',
          id: rental.id,
          delete: 1
        };
        await callWFAPI(0, 'zohoCreator', data);
        const updatedRentals = rentals.filter(r => r.id !== rental.id);
        setRentals(updatedRentals);
        if (selectedRental?.id === rental.id) {
          setSelectedRental(null);
        }
        await logActivity('rental', 'Rental deleted', `Deleted rental: ${rental.name} - ${rental.eventName} ($${rental.total.toFixed(2)})`);
      } catch (error) {
        console.error('Error deleting rental:', error);
        alert('Failed to delete rental. Please try again.');
      }
    }
  };
  const openAssignModal = (rental: Rental) => {
    setShowAssignModal(true);
    setAssigningTechnician(rental.assignedTechnician || '');
    setAssigningDistributor(rental.assignedDistributor || '');
  };
  const handleTechnicianChange = (techName: string) => {
    setAssigningTechnician(techName);
    const tech = technicians.find(t => t.name === techName);
    if (tech) {
      setAssigningDistributor(tech.distributorName);
    }
  };
  const saveAssignment = async () => {
    if (!selectedRental || !assigningTechnician || !assigningDistributor) {
      alert('Please select both a technician and distributor');
      return;
    }
    try {
      const tech = technicians.find(t => t.name === assigningTechnician);
      if (!tech) {
        alert('Selected technician not found');
        return;
      }
      const data = {
        appname: 'tkd-score',
        reportname: 'Gen_3_Scoring_Rental_Report',
        id: selectedRental.id,
        params: JSON.stringify({
          Technician: tech.id
        })
      };
      await callWFAPI(0, 'zohoCreator', data);
      const updatedRentals = rentals.map(rental => rental.id === selectedRental.id ? {
        ...rental,
        assignedTechnician: assigningTechnician,
        assignedDistributor: assigningDistributor
      } : rental);
      setRentals(updatedRentals);
      setSelectedRental({
        ...selectedRental,
        assignedTechnician: assigningTechnician,
        assignedDistributor: assigningDistributor
      });
      logActivity('rental', 'Technician assigned', `Assigned ${assigningTechnician} (${assigningDistributor}) to ${selectedRental.eventName} - ${selectedRental.name}`);
      setShowAssignModal(false);
    } catch (error) {
      console.error('Error assigning technician:', error);
      alert('Failed to assign technician. Please try again.');
    }
  };
  const startEditingDates = () => {
    if (selectedRental) {
      // Convert date strings to YYYY-MM-DD format for date input
      // Ensure proper year format (YYYY)
      const formatDateForInput = (dateString: string): string => {
        if (!dateString) return '';
        // If date already includes time, split it
        const datePart = dateString.split('T')[0];
        // Parse the date and ensure it's in YYYY-MM-DD format
        const date = new Date(datePart + 'T00:00:00');
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      setEditedProposedDate(formatDateForInput(selectedRental.proposedEventDate));
      setEditedAlternateDate(formatDateForInput(selectedRental.alternateDate));
      setEditedNumberOfDays(selectedRental.selections.numberOfDays);
      setEditingDates(true);
    }
  };
  const saveDates = async () => {
    if (!selectedRental) return;
    try {
      // Ensure dates are in proper YYYY-MM-DD format before saving
      const formatDateForDatabase = (dateString: string): string => {
        if (!dateString) return '';
        // Date input gives us YYYY-MM-DD format, ensure it's valid
        const date = new Date(dateString + 'T00:00:00');
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      const formattedProposedDate = formatDateForDatabase(editedProposedDate);
      const formattedAlternateDate = formatDateForDatabase(editedAlternateDate);
      // Update rental dates and number of days
      const data = {
        appname: 'tkd-score',
        reportname: 'Gen_3_Scoring_Rental_Report',
        id: selectedRental.id,
        params: JSON.stringify({
          Proposed_Event_Date: formattedProposedDate,
          Alternate_Date: formattedAlternateDate,
          Number_Of_Days: editedNumberOfDays
        })
      };
      await callWFAPI(0, 'zohoCreator', data);
      // If rental has an invoice, update the invoice dates too
      if (selectedRental.invoiceId) {
        const invoiceData = {
          appname: 'tkd-score',
          reportname: 'All_Rental_Invoices',
          id: selectedRental.invoiceId,
          params: JSON.stringify({
            Event_Date: formattedProposedDate
          })
        };
        await callWFAPI(0, 'zohoCreator', invoiceData);
        await loadInvoices();
      }
      // Reload rentals from database to ensure we have the latest data
      await loadRentals();
      // Update local state
      const updatedRentals = rentals.map(rental => rental.id === selectedRental.id ? {
        ...rental,
        proposedEventDate: formattedProposedDate,
        alternateDate: formattedAlternateDate,
        selections: {
          ...rental.selections,
          numberOfDays: editedNumberOfDays
        }
      } : rental);
      setRentals(updatedRentals);
      setSelectedRental({
        ...selectedRental,
        proposedEventDate: formattedProposedDate,
        alternateDate: formattedAlternateDate,
        selections: {
          ...selectedRental.selections,
          numberOfDays: editedNumberOfDays
        }
      });
      logActivity('rental', 'Event dates updated', `Updated dates and duration for ${selectedRental.eventName} - ${selectedRental.name} (${editedNumberOfDays} days)${selectedRental.invoiceId ? ' (invoice updated)' : ''}`);
      setEditingDates(false);
      // Show success message
      alert('Event dates and duration updated successfully!');
    } catch (error) {
      console.error('Error updating dates:', error);
      alert('Failed to update dates. Please try again.');
    }
  };
  const cancelEditingDates = () => {
    setEditingDates(false);
    setEditedProposedDate('');
    setEditedAlternateDate('');
    setEditedNumberOfDays(1);
  };
  // Distributor handlers
  const handleAddDistributor = async (name: string, rings: number, publishableKey: string, secretKey: string) => {
    try {
      const data = {
        appname: 'tkd-score',
        formname: 'Distributor',
        params: JSON.stringify({
          Name: name,
          Rings: rings,
          Stripe_Public_Key: publishableKey,
          Stripe_Private_Key: secretKey
        })
      };
      const newDistributor = await callWFAPI(0, 'zohoCreator', data);
      await loadDistributors();
      logActivity('distributor', 'Distributor added', `Added distributor: ${name} with ${rings} rings`);
    } catch (error) {
      console.error('Error adding distributor:', error);
      alert('Failed to add distributor. Please try again.');
    }
  };
  const handleEditDistributor = async (index: number, name: string, rings: number, publishableKey: string, secretKey: string) => {
    const oldDist = distributors[index];
    try {
      const data = {
        appname: 'tkd-score',
        reportname: 'All_Distributors',
        id: oldDist.id,
        params: JSON.stringify({
          Name: name,
          Rings: rings,
          Stripe_Public_Key: publishableKey,
          Stripe_Private_Key: secretKey
        })
      };
      await callWFAPI(0, 'zohoCreator', data);
      await loadDistributors();
      logActivity('distributor', 'Distributor updated', `Updated distributor: ${oldDist.name} → ${name}, rings: ${oldDist.rings} → ${rings}`);
    } catch (error) {
      console.error('Error updating distributor:', error);
      alert('Failed to update distributor. Please try again.');
    }
  };
  const handleDeleteDistributor = async (index: number) => {
    const dist = distributors[index];
    if (!window.confirm(`Are you sure you want to delete ${dist.name}?`)) {
      return;
    }
    try {
      const data = {
        appname: 'tkd-score',
        reportname: 'All_Distributors',
        id: dist.id,
        delete: 1
      };
      await callWFAPI(0, 'zohoCreator', data);
      await loadDistributors();
      logActivity('distributor', 'Distributor deleted', `Deleted distributor: ${dist.name} (${dist.rings} rings)`);
    } catch (error) {
      console.error('Error deleting distributor:', error);
      alert('Failed to delete distributor. Please try again.');
    }
  };
  // Technician handlers
  const handleAddTechnician = async (name: string, distributorName: string, email: string, phone: string) => {
    try {
      const distributor = distributors.find(d => d.name === distributorName);
      if (!distributor) {
        alert('Selected distributor not found');
        return;
      }
      const formattedPhone = formatPhoneNumber(phone);
      const data = {
        appname: 'tkd-score',
        formname: 'Technician',
        params: JSON.stringify({
          Name: name,
          Distributor: distributor.id,
          Email: email,
          Phone_Number: formattedPhone
        })
      };
      await callWFAPI(0, 'zohoCreator', data);
      await loadTechnicians();
      logActivity('technician', 'Technician added', `Added technician: ${name} (${distributorName}) - ${email}`);
    } catch (error) {
      console.error('Error adding technician:', error);
      alert('Failed to add technician. Please try again.');
    }
  };
  const handleEditTechnician = async (index: number, name: string, distributorName: string, email: string, phone: string) => {
    const oldTech = technicians[index];
    try {
      const distributor = distributors.find(d => d.name === distributorName);
      if (!distributor) {
        alert('Selected distributor not found');
        return;
      }
      const formattedPhone = formatPhoneNumber(phone);
      const data = {
        appname: 'tkd-score',
        reportname: 'All_Technicians',
        id: oldTech.id,
        params: JSON.stringify({
          Name: name,
          Distributor: distributor.id,
          Email: email,
          Phone_Number: formattedPhone
        })
      };
      await callWFAPI(0, 'zohoCreator', data);
      await loadTechnicians();
      logActivity('technician', 'Technician updated', `Updated technician: ${oldTech.name} → ${name}, distributor: ${oldTech.distributorName} → ${distributorName}`);
    } catch (error) {
      console.error('Error updating technician:', error);
      alert('Failed to update technician. Please try again.');
    }
  };
  const handleDeleteTechnician = async (index: number) => {
    const tech = technicians[index];
    if (!window.confirm(`Are you sure you want to delete ${tech.name}?`)) {
      return;
    }
    try {
      const data = {
        appname: 'tkd-score',
        reportname: 'All_Technicians',
        id: tech.id,
        delete: 1
      };
      await callWFAPI(0, 'zohoCreator', data);
      await loadTechnicians();
      logActivity('technician', 'Technician deleted', `Deleted technician: ${tech.name} (${tech.distributorName})`);
    } catch (error) {
      console.error('Error deleting technician:', error);
      alert('Failed to delete technician. Please try again.');
    }
  };
  return <div className="min-h-screen bg-slate-100" data-id="element-91">
      <div className="bg-white shadow-sm border-b border-slate-200" data-id="element-92">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center" data-id="element-93">
          <div className="flex items-center gap-4" data-id="element-94">
            <button onClick={() => navigate('/')} className="flex items-center text-slate-600 hover:text-slate-800 transition-colors" data-id="element-95">
              <ArrowLeft size={20} className="mr-2" data-id="element-96" />
              Back to Rental Form
            </button>
          </div>
          <h1 className="text-xl font-bold text-slate-800" data-id="element-97">Admin Panel</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6" data-id="element-98">
        {/* Tabs Navigation */}
        <div className="bg-white rounded-t-lg border-b border-slate-200" data-id="element-99">
          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center justify-between p-4 border-b border-slate-200" data-id="element-100">
            <h2 className="font-semibold text-slate-800" data-id="element-101">
              {activeTab === 'rentals' && 'Rentals'}
              {activeTab === 'calendar' && 'Calendar'}
              {activeTab === 'invoices' && 'Invoices'}
              {activeTab === 'distributors' && 'Distributors'}
              {activeTab === 'technicians' && 'Technicians'}
              {activeTab === 'log' && 'Activity Log'}
              {activeTab === 'map' && 'Map'}
              {activeTab === 'dispatch' && 'Dispatch'}
              {activeTab === 'onsiteprep' && 'Onsite Prep'}
              {activeTab === 'notifications' && 'Notifications'}
              {activeTab === 'sales' && 'Sales Report'}
            </h2>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-102">
              {mobileMenuOpen ? <X size={24} data-id="element-103" /> : <Menu size={24} data-id="element-104" />}
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && <div className="lg:hidden border-b border-slate-200" data-id="element-105">
              <div className="border-b border-slate-200" data-id="element-106">
                <div className="px-4 py-2 bg-slate-100" data-id="element-107">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-108">
                    Core Operations
                  </span>
                </div>
                <button onClick={() => {
              setActiveTab('rentals');
              setMobileMenuOpen(false);
            }} className={`w-full flex items-center gap-3 px-6 py-3 font-medium transition-colors ${activeTab === 'rentals' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-109">
                  <Package size={18} data-id="element-110" />
                  Rentals
                </button>
                <button onClick={() => {
              setActiveTab('invoices');
              setMobileMenuOpen(false);
              loadInvoices();
            }} className={`w-full flex items-center gap-3 px-6 py-3 font-medium transition-colors ${activeTab === 'invoices' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-111">
                  <FileText size={18} data-id="element-112" />
                  Invoices
                </button>
                <button onClick={() => {
              setActiveTab('calendar');
              setMobileMenuOpen(false);
            }} className={`w-full flex items-center gap-3 px-6 py-3 font-medium transition-colors ${activeTab === 'calendar' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-113">
                  <Calendar size={18} data-id="element-114" />
                  Calendar
                </button>
                <button onClick={() => {
              setActiveTab('map');
              setMobileMenuOpen(false);
            }} className={`w-full flex items-center gap-3 px-6 py-3 font-medium transition-colors ${activeTab === 'map' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-115">
                  <MapPin size={18} data-id="element-116" />
                  Map
                </button>
              </div>

              <div className="border-b border-slate-200" data-id="element-117">
                <div className="px-4 py-2 bg-slate-100" data-id="element-118">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-119">
                    Equipment
                  </span>
                </div>
                <button onClick={() => {
              setActiveTab('dispatch');
              setMobileMenuOpen(false);
            }} className={`w-full flex items-center gap-3 px-6 py-3 font-medium transition-colors ${activeTab === 'dispatch' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-120">
                  <Truck size={18} data-id="element-121" />
                  Dispatch
                </button>
                <button onClick={() => {
              setActiveTab('onsiteprep');
              setMobileMenuOpen(false);
            }} className={`w-full flex items-center gap-3 px-6 py-3 font-medium transition-colors ${activeTab === 'onsiteprep' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-122">
                  <CheckCircle size={18} data-id="element-123" />
                  Onsite Prep
                </button>
              </div>

              <div className="border-b border-slate-200" data-id="element-124">
                <div className="px-4 py-2 bg-slate-100" data-id="element-125">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-126">
                    Reports
                  </span>
                </div>
                <button onClick={() => {
              setActiveTab('sales');
              setMobileMenuOpen(false);
            }} className={`w-full flex items-center gap-3 px-6 py-3 font-medium transition-colors ${activeTab === 'sales' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-127">
                  <BarChart3 size={18} data-id="element-128" />
                  Sales Report
                </button>
              </div>

              <div data-id="element-129">
                <div className="px-4 py-2 bg-slate-100" data-id="element-130">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-131">
                    Settings
                  </span>
                </div>
                <button onClick={() => {
              setActiveTab('distributors');
              setMobileMenuOpen(false);
            }} className={`w-full flex items-center gap-3 px-6 py-3 font-medium transition-colors ${activeTab === 'distributors' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-132">
                  <Package size={18} data-id="element-133" />
                  Distributors
                </button>
                <button onClick={() => {
              setActiveTab('technicians');
              setMobileMenuOpen(false);
            }} className={`w-full flex items-center gap-3 px-6 py-3 font-medium transition-colors ${activeTab === 'technicians' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-134">
                  <Users size={18} data-id="element-135" />
                  Technicians
                </button>
                <button onClick={() => {
              setActiveTab('notifications');
              setMobileMenuOpen(false);
            }} className={`w-full flex items-center gap-3 px-6 py-3 font-medium transition-colors ${activeTab === 'notifications' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-136">
                  <Mail size={18} data-id="element-137" />
                  Notifications
                </button>
                <button onClick={() => {
              setActiveTab('log');
              setMobileMenuOpen(false);
            }} className={`w-full flex items-center gap-3 px-6 py-3 font-medium transition-colors ${activeTab === 'log' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-138">
                  <Activity size={18} data-id="element-139" />
                  Activity Log
                </button>
              </div>
            </div>}

          {/* Desktop Menu */}
          <div className="hidden lg:block" data-id="element-140">
            <div className="border-b border-slate-200" data-id="element-141">
              <div className="px-4 py-2 bg-slate-50" data-id="element-142">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-143">
                  Core Operations
                </span>
              </div>
              <div className="grid grid-cols-4 gap-0" data-id="element-144">
                <button onClick={() => setActiveTab('rentals')} className={`flex items-center justify-center gap-2 px-4 py-3 font-medium transition-colors relative border-r border-slate-200 ${activeTab === 'rentals' ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-145">
                  <Package size={18} data-id="element-146" />
                  <span className="hidden xl:inline" data-id="element-147">Rentals</span>
                  {activeTab === 'rentals' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" data-id="element-148"></div>}
                </button>
                <button onClick={() => {
                setActiveTab('invoices');
                loadInvoices();
              }} className={`flex items-center justify-center gap-2 px-4 py-3 font-medium transition-colors relative border-r border-slate-200 ${activeTab === 'invoices' ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-149">
                  <FileText size={18} data-id="element-150" />
                  <span className="hidden xl:inline" data-id="element-151">Invoices</span>
                  {activeTab === 'invoices' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" data-id="element-152"></div>}
                </button>
                <button onClick={() => setActiveTab('calendar')} className={`flex items-center justify-center gap-2 px-4 py-3 font-medium transition-colors relative border-r border-slate-200 ${activeTab === 'calendar' ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-153">
                  <Calendar size={18} data-id="element-154" />
                  <span className="hidden xl:inline" data-id="element-155">Calendar</span>
                  {activeTab === 'calendar' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" data-id="element-156"></div>}
                </button>
                <button onClick={() => setActiveTab('map')} className={`flex items-center justify-center gap-2 px-4 py-3 font-medium transition-colors relative ${activeTab === 'map' ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-157">
                  <MapPin size={18} data-id="element-158" />
                  <span className="hidden xl:inline" data-id="element-159">Map</span>
                  {activeTab === 'map' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" data-id="element-160"></div>}
                </button>
              </div>
            </div>

            <div className="border-b border-slate-200" data-id="element-161">
              <div className="px-4 py-2 bg-slate-50" data-id="element-162">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-163">
                  Equipment
                </span>
              </div>
              <div className="grid grid-cols-2 gap-0" data-id="element-164">
                <button onClick={() => setActiveTab('dispatch')} className={`flex items-center justify-center gap-2 px-4 py-3 font-medium transition-colors relative border-r border-slate-200 ${activeTab === 'dispatch' ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-165">
                  <Truck size={18} data-id="element-166" />
                  <span className="hidden xl:inline" data-id="element-167">Dispatch</span>
                  {activeTab === 'dispatch' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" data-id="element-168"></div>}
                </button>
                <button onClick={() => setActiveTab('onsiteprep')} className={`flex items-center justify-center gap-2 px-4 py-3 font-medium transition-colors relative ${activeTab === 'onsiteprep' ? 'text-blue-500 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-169">
                  <CheckCircle size={18} data-id="element-170" />
                  <span className="hidden xl:inline" data-id="element-171">Onsite Prep</span>
                  {activeTab === 'onsiteprep' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" data-id="element-172"></div>}
                </button>
              </div>
            </div>

            <div className="border-b border-slate-200" data-id="element-173">
              <div className="px-4 py-2 bg-slate-50" data-id="element-174">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-175">
                  Reports
                </span>
              </div>
              <div className="grid grid-cols-1 gap-0" data-id="element-176">
                <button onClick={() => setActiveTab('sales')} className={`flex items-center justify-center gap-2 px-4 py-3 font-medium transition-colors relative ${activeTab === 'sales' ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-177">
                  <BarChart3 size={18} data-id="element-178" />
                  <span className="hidden xl:inline" data-id="element-179">Sales Report</span>
                  {activeTab === 'sales' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" data-id="element-180"></div>}
                </button>
              </div>
            </div>

            <div data-id="element-181">
              <div className="px-4 py-2 bg-slate-50" data-id="element-182">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-183">
                  Settings
                </span>
              </div>
              <div className="grid grid-cols-4 gap-0" data-id="element-184">
                <button onClick={() => setActiveTab('distributors')} className={`flex items-center justify-center gap-2 px-4 py-3 font-medium transition-colors relative border-r border-slate-200 ${activeTab === 'distributors' ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-185">
                  <Package size={18} data-id="element-186" />
                  <span className="hidden xl:inline" data-id="element-187">Distributors</span>
                  {activeTab === 'distributors' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" data-id="element-188"></div>}
                </button>
                <button onClick={() => setActiveTab('technicians')} className={`flex items-center justify-center gap-2 px-4 py-3 font-medium transition-colors relative border-r border-slate-200 ${activeTab === 'technicians' ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-189">
                  <Users size={18} data-id="element-190" />
                  <span className="hidden xl:inline" data-id="element-191">Technicians</span>
                  {activeTab === 'technicians' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" data-id="element-192"></div>}
                </button>
                <button onClick={() => setActiveTab('notifications')} className={`flex items-center justify-center gap-2 px-4 py-3 font-medium transition-colors relative border-r border-slate-200 ${activeTab === 'notifications' ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-193">
                  <Mail size={18} data-id="element-194" />
                  <span className="hidden xl:inline" data-id="element-195">Notifications</span>
                  {activeTab === 'notifications' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" data-id="element-196"></div>}
                </button>
                <button onClick={() => setActiveTab('log')} className={`flex items-center justify-center gap-2 px-4 py-3 font-medium transition-colors relative ${activeTab === 'log' ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-197">
                  <Activity size={18} data-id="element-198" />
                  <span className="hidden xl:inline" data-id="element-199">Activity Log</span>
                  {activeTab === 'log' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" data-id="element-200"></div>}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-b-lg shadow-md" data-id="element-201">
          {activeTab === 'rentals' && <RentalsManager rentals={rentals} filter={filter} setFilter={setFilter} onRentalClick={setSelectedRental} onCreateInvoice={createInvoiceFromRental} onAssignTechnician={rental => {
          setSelectedRental(rental);
          openAssignModal(rental);
        }} creatingInvoice={creatingInvoice} hasInvoice={hasInvoice} getStatusColor={getStatusColor} getDistributorColor={getDistributorColor} onNotesUpdated={loadRentals} data-id="element-202" />}
          {activeTab === 'calendar' && <CalendarView rentals={rentals} onRentalClick={setSelectedRental} getStatusColor={getStatusColor} getStatusBadgeColor={getStatusBadgeColor} getCoastFromLocation={getCoastFromLocation} data-id="element-203" />}
          {activeTab === 'invoices' && <div className="p-6" data-id="element-204">
              <InvoiceManager rentals={rentals} invoices={invoices} onInvoiceDeleted={() => {
            loadInvoices();
            loadRentals();
          }} onNotesUpdated={loadRentals} data-id="element-205" />
            </div>}
          {activeTab === 'distributors' && <DistributorsManager distributors={distributors} onAdd={handleAddDistributor} onEdit={handleEditDistributor} onDelete={handleDeleteDistributor} getDistributorColor={getDistributorColor} data-id="element-206" />}
          {activeTab === 'technicians' && <TechniciansManager technicians={technicians} distributors={distributors} onAdd={handleAddTechnician} onEdit={handleEditTechnician} onDelete={handleDeleteTechnician} getDistributorColor={getDistributorColor} data-id="element-207" />}
          {activeTab === 'log' && <ActivityLog logs={activityLogs} filter={logFilter} setFilter={setLogFilter} data-id="element-208" />}
          {activeTab === 'map' && <div className="p-6" data-id="element-209">
              <USMap events={rentals} onEventClick={event => setSelectedRental(event)} data-id="element-210" />
            </div>}
          {activeTab === 'dispatch' && <DispatchManager rentals={rentals} getStatusColor={getStatusColor} logActivity={logActivity} onNotesUpdated={loadRentals} data-id="element-211" />}
          {activeTab === 'onsiteprep' && <div className="p-6" data-id="element-212">
              <OnsitePrep rentals={rentals} onNotesUpdated={loadRentals} data-id="element-213" />
            </div>}
          {activeTab === 'notifications' && <div className="p-6" data-id="element-214">
              <NotificationTemplates data-id="element-215" />
            </div>}
          {activeTab === 'sales' && <SalesReport data-id="element-216" />}
        </div>
      </div>

      {/* Rental Detail Modal */}
      {selectedRental && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-217">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-218">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between" data-id="element-219">
              <h3 className="text-xl font-bold text-slate-800" data-id="element-220">
                Rental Details
              </h3>
              <div className="flex items-center gap-2" data-id="element-221">
                <InternalNotesButton rentalId={selectedRental.id} initialNotes={selectedRental.internalNotes} onNotesSaved={loadRentals} data-id="element-222" />
                <button onClick={() => deleteRental(selectedRental)} disabled={!!selectedRental.invoiceId} className={`p-2 rounded-lg transition-colors ${selectedRental.invoiceId ? 'text-slate-400 cursor-not-allowed' : 'text-red-600 hover:bg-red-50'}`} title={selectedRental.invoiceId ? 'Cannot delete rental with invoice' : 'Delete Rental'} data-id="element-223">
                  <Trash2 size={18} data-id="element-224" />
                </button>
                <button onClick={() => setSelectedRental(null)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-225">
                  <X size={20} className="text-slate-600" data-id="element-226" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6" data-id="element-227">
              <div data-id="element-228">
                <label className="text-sm font-medium text-slate-500" data-id="element-229">
                  Status
                </label>
                <div className="mt-1" data-id="element-230">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(selectedRental.status)}`} data-id="element-231">
                    {selectedRental.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-232">
                <div data-id="element-233">
                  <label className="text-sm font-medium text-slate-500" data-id="element-234">
                    Name
                  </label>
                  <p className="mt-1 text-slate-800 font-medium" data-id="element-235">
                    {selectedRental.name}
                  </p>
                </div>
                <div data-id="element-236">
                  <label className="text-sm font-medium text-slate-500" data-id="element-237">
                    Phone
                  </label>
                  <p className="mt-1 text-slate-800" data-id="element-238">{selectedRental.phone}</p>
                </div>
              </div>

              <div data-id="element-239">
                <label className="text-sm font-medium text-slate-500" data-id="element-240">
                  Email
                </label>
                <p className="mt-1 text-slate-800" data-id="element-241">{selectedRental.email}</p>
              </div>

              <div data-id="element-242">
                <label className="text-sm font-medium text-slate-500" data-id="element-243">
                  Event Name
                </label>
                <p className="mt-1 text-slate-800 font-medium" data-id="element-244">
                  {selectedRental.eventName}
                </p>
              </div>

              <div data-id="element-245">
                <label className="text-sm font-medium text-slate-500" data-id="element-246">
                  Shipping Address
                </label>
                <div className="mt-1 text-slate-800 space-y-1" data-id="element-247">
                  {selectedRental.customerInfo?.address && <p data-id="element-248">{selectedRental.customerInfo.address}</p>}
                  {selectedRental.customerInfo?.address2 && <p data-id="element-249">{selectedRental.customerInfo.address2}</p>}
                  {(selectedRental.customerInfo?.city || selectedRental.customerInfo?.state || selectedRental.customerInfo?.zip) && <p data-id="element-250">
                      {[selectedRental.customerInfo?.city, selectedRental.customerInfo?.state, selectedRental.customerInfo?.zip].filter(Boolean).join(', ')}
                    </p>}
                  {!selectedRental.customerInfo?.address && !selectedRental.customerInfo?.city && <p className="text-slate-500" data-id="element-251">No address provided</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-252">
                <div data-id="element-253">
                  <label className="text-sm font-medium text-slate-500" data-id="element-254">
                    Proposed Date
                  </label>
                  {editingDates ? <input type="date" value={editedProposedDate} onChange={e => setEditedProposedDate(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-255" /> : <p className="mt-1 text-slate-800" data-id="element-256">
                      {selectedRental.proposedEventDate ? (() => {
                  const date = new Date(selectedRental.proposedEventDate + 'T00:00:00');
                  return isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                  });
                })() : 'Not set'}
                    </p>}
                </div>
                <div data-id="element-257">
                  <label className="text-sm font-medium text-slate-500" data-id="element-258">
                    Alternate Date
                  </label>
                  {editingDates ? <input type="date" value={editedAlternateDate} onChange={e => setEditedAlternateDate(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-259" /> : <p className="mt-1 text-slate-800" data-id="element-260">
                      {selectedRental.alternateDate ? (() => {
                  const date = new Date(selectedRental.alternateDate + 'T00:00:00');
                  return isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                  });
                })() : 'Not set'}
                    </p>}
                </div>
              </div>

              {editingDates && <div data-id="element-261">
                  <label className="text-sm font-medium text-slate-500" data-id="element-262">
                    Number of Days
                  </label>
                  <input type="number" min="1" max="7" value={editedNumberOfDays} onChange={e => setEditedNumberOfDays(parseInt(e.target.value) || 1)} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-263" />
                  <p className="text-xs text-slate-500 mt-1" data-id="element-264">
                    Event duration (1-7 days)
                  </p>
                </div>}

              {editingDates ? <div className="flex gap-3" data-id="element-265">
                  <button onClick={saveDates} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium" data-id="element-266">
                    Save Dates & Duration
                  </button>
                  <button onClick={cancelEditingDates} className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-267">
                    Cancel
                  </button>
                </div> : <button onClick={startEditingDates} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2" data-id="element-268">
                  <Calendar size={16} data-id="element-269" />
                  Edit Dates & Duration
                </button>}

              <div data-id="element-270">
                <label className="text-sm font-medium text-slate-500 mb-2 block" data-id="element-271">
                  Equipment
                </label>
                <div className="bg-slate-50 rounded-lg p-4 space-y-2" data-id="element-272">
                  {selectedRental.selections.fullGen3Ring > 0 && <div className="flex justify-between" data-id="element-273">
                      <span className="text-slate-700" data-id="element-274">Full Gen 3 Ring</span>
                      <span className="font-medium text-slate-800" data-id="element-275">
                        {selectedRental.selections.fullGen3Ring}
                      </span>
                    </div>}
                  {selectedRental.selections.gen3Body > 0 && <div className="flex justify-between" data-id="element-276">
                      <span className="text-slate-700" data-id="element-277">Gen 3 Body</span>
                      <span className="font-medium text-slate-800" data-id="element-278">
                        {selectedRental.selections.gen3Body}
                      </span>
                    </div>}
                  {selectedRental.selections.gen3Head > 0 && <div className="flex justify-between" data-id="element-279">
                      <span className="text-slate-700" data-id="element-280">Gen 3 Head</span>
                      <span className="font-medium text-slate-800" data-id="element-281">
                        {selectedRental.selections.gen3Head}
                      </span>
                    </div>}
                  {selectedRental.selections.gen3JudgesOnly > 0 && <div className="flex justify-between" data-id="element-282">
                      <span className="text-slate-700" data-id="element-283">Judges Only</span>
                      <span className="font-medium text-slate-800" data-id="element-284">
                        {selectedRental.selections.gen3JudgesOnly}
                      </span>
                    </div>}
                  {selectedRental.selections.requireTech && <div className="flex justify-between items-center pt-2 border-t border-slate-200" data-id="element-285">
                      <span className="text-slate-700" data-id="element-286">On-site Technician</span>
                      <span className="font-medium text-red-600" data-id="element-287">Required</span>
                    </div>}
                  {selectedRental.selections.numberOfDays > 1 && <div className="flex justify-between items-center pt-2 border-t border-slate-200" data-id="element-288">
                      <span className="text-slate-700" data-id="element-289">Event Duration</span>
                      <span className="inline-flex items-center gap-1 font-medium text-blue-600" data-id="element-290">
                        <CheckCircle size={16} data-id="element-291" />
                        {selectedRental.selections.numberOfDays} Days
                      </span>
                    </div>}
                </div>

                {/* Technician Assignment Status */}
                {selectedRental.assignedTechnician && <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-3" data-id="element-292">
                    <div className="flex items-center justify-between" data-id="element-293">
                      <div data-id="element-294">
                        <span className="text-xs font-medium text-purple-600" data-id="element-295">
                          Assigned Technician
                        </span>
                        <p className="text-sm font-semibold text-purple-900" data-id="element-296">
                          {selectedRental.assignedTechnician}
                        </p>
                      </div>
                      {selectedRental.assignedDistributor && <span className={`px-2 py-1 rounded text-xs font-medium border ${getDistributorColor(selectedRental.assignedDistributor)}`} data-id="element-297">
                          {selectedRental.assignedDistributor}
                        </span>}
                    </div>
                  </div>}

                {(selectedRental.selections.requireTech || selectedRental.selections.fullGen3Ring > 0 || selectedRental.selections.gen3Body > 0 || selectedRental.selections.gen3Head > 0 || selectedRental.selections.gen3JudgesOnly > 0) && <div className="mt-4" data-id="element-298">
                    <button onClick={() => openAssignModal(selectedRental)} disabled={hasInvoice(selectedRental.id)} className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium ${hasInvoice(selectedRental.id) ? 'bg-slate-200 text-slate-500 cursor-not-allowed' : selectedRental.assignedTechnician ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-id="element-299">
                      <Users size={16} data-id="element-300" />
                      {selectedRental.assignedTechnician ? 'Change Technician' : 'Assign Technician'}
                    </button>
                    {hasInvoice(selectedRental.id) && <p className="text-xs text-red-600 text-center mt-2" data-id="element-301">
                        Remove the invoice to re-assign distributor
                      </p>}
                  </div>}
              </div>

              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200" data-id="element-302">
                <div className="flex justify-between items-center" data-id="element-303">
                  <span className="text-blue-800 font-medium" data-id="element-304">
                    Total Amount
                  </span>
                  <span className="text-2xl font-bold text-blue-900" data-id="element-305">
                    ${selectedRental.total.toFixed(2)}
                  </span>
                </div>
              </div>

              <div data-id="element-306">
                <label className="text-sm font-medium text-slate-500" data-id="element-307">
                  Submitted
                </label>
                <p className="mt-1 text-slate-600 text-sm" data-id="element-308">
                  {new Date(selectedRental.added).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>}

      {/* Assign Distributor Modal */}
      {showAssignModal && selectedRental && <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4" data-id="element-309">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md" data-id="element-310">
            <div className="bg-white border-b border-slate-200 p-6 flex items-center justify-between rounded-t-lg" data-id="element-311">
              <h3 className="text-xl font-bold text-slate-800" data-id="element-312">
                Assign Technician
              </h3>
              <button onClick={() => setShowAssignModal(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-313">
                <X size={20} className="text-slate-600" data-id="element-314" />
              </button>
            </div>

            <div className="p-6 space-y-4" data-id="element-315">
              <div data-id="element-316">
                <p className="text-sm text-slate-600 mb-4" data-id="element-317">
                  Assign a technician to{' '}
                  <strong data-id="element-318">{selectedRental.eventName}</strong>. The distributor
                  will be automatically assigned based on the technician.
                </p>
              </div>

              <div data-id="element-319">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-320">
                  Select Technician <span className="text-red-500" data-id="element-321">*</span>
                </label>
                <select value={assigningTechnician} onChange={e => handleTechnicianChange(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-322">
                  {technicians.map(tech => <option key={tech.name} value={tech.name} data-id="element-323">
                      {tech.name}
                    </option>)}
                </select>
              </div>

              {assigningTechnician && assigningDistributor && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-324">
                  <div className="space-y-2" data-id="element-325">
                    <div className="flex items-center justify-between" data-id="element-326">
                      <span className="text-sm text-blue-700" data-id="element-327">Technician:</span>
                      <span className="font-semibold text-blue-900" data-id="element-328">
                        {assigningTechnician}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-329">
                      <span className="text-sm text-blue-700" data-id="element-330">
                        Distributor:
                      </span>
                      <span className={`px-2 py-1 rounded text-sm font-medium border ${getDistributorColor(assigningDistributor)}`} data-id="element-331">
                        {assigningDistributor}
                      </span>
                    </div>
                  </div>
                </div>}

              <div className="flex gap-3 pt-4" data-id="element-332">
                <button onClick={() => setShowAssignModal(false)} className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-333">
                  Cancel
                </button>
                <button onClick={saveAssignment} disabled={!assigningTechnician || !assigningDistributor} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-slate-300 disabled:cursor-not-allowed" data-id="element-334">
                  Assign
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}